import { useCallback, useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { motion, useScroll, useTransform } from "framer-motion";
import "@/App.css";
import { ArrowUpRight, ChevronRight, Download, Heart, Menu, Play, Search, X } from "lucide-react";
import { Toaster, toast } from "sonner";
import { LogoMark } from "@/components/site/logo";
import { MaskLine, Reveal } from "@/components/site/reveal";
import { Marquee } from "@/components/site/marquee";
import { PropertyModal } from "@/components/site/property-modal";
import { CalculatorModal, ConsultModal, ShortlistModal } from "@/components/site/modals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1682184805271-11671b7ecf4c?auto=format&fit=crop&w=1800&q=88",
  living: "https://images.unsplash.com/photo-1724582586495-d050726cf354?auto=format&fit=crop&w=1200&q=85",
  bedroom: "https://images.unsplash.com/photo-1710883734891-93709398496d?auto=format&fit=crop&w=1200&q=85",
  team: "https://images.unsplash.com/photo-1676694047732-768cea5b66eb",
  meeting: "https://images.unsplash.com/photo-1774600122432-dfddc9987ff8",
};

const FALLBACK_PROPERTIES = [
  { id: 1, name: "The Glass House", location: "Al Barari · Dubai", neighborhood: "Al Barari", type: "Villa", price: "AED 12.5M", price_value: 12500000, beds: 5, area: "6,480 sq ft", image: IMAGES.hero, gallery: [IMAGES.living, IMAGES.bedroom, IMAGES.meeting], description: "A quietly confident five-bedroom residence where architecture, light and landscape meet.", status: "Available" },
  { id: 2, name: "Casa Maré", location: "Palm Jumeirah · Dubai", neighborhood: "Palm Jumeirah", type: "Villa", price: "AED 8.2M", price_value: 8200000, beds: 4, area: "4,120 sq ft", image: IMAGES.living, gallery: [IMAGES.hero, IMAGES.bedroom, IMAGES.team], description: "A warm, composed family villa with generous entertaining spaces and a private garden.", status: "Available" },
  { id: 3, name: "The Observatory", location: "Downtown · Dubai", neighborhood: "Downtown", type: "Apartment", price: "AED 3.8M", price_value: 3800000, beds: 3, area: "2,240 sq ft", image: IMAGES.bedroom, gallery: [IMAGES.living, IMAGES.hero, IMAGES.meeting], description: "A considered city apartment with a clear skyline outlook and effortless access to Downtown.", status: "Available" },
];

const MARQUEE_ITEMS = ["Al Barari", "Palm Jumeirah", "Downtown Dubai", "Emirates Hills", "Jumeirah Bay", "DIFC"];
const PRICE_BANDS = [
  { label: "Any price" },
  { label: "Under AED 5M", max: 5000000 },
  { label: "AED 5M – 10M", min: 5000000, max: 10000000 },
  { label: "AED 10M +", min: 10000000 },
];
const BED_OPTIONS = ["Any", "3+", "4+", "5+"];

const loadShortlist = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem("brk-shortlist") || "[]");
    return Array.isArray(parsed) ? parsed.filter((item) => item && typeof item === "object" && item.id !== undefined) : [];
  } catch {
    return [];
  }
};

function Site() {
  const [properties, setProperties] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");
  const [neighborhood, setNeighborhood] = useState("All");
  const [beds, setBeds] = useState("Any");
  const [priceBand, setPriceBand] = useState("Any price");
  const [neighborhoods, setNeighborhoods] = useState(["Al Barari", "Palm Jumeirah", "Downtown"]);
  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const [shortlist, setShortlist] = useState(loadShortlist);
  const [loading, setLoading] = useState(true);
  const lenisRef = useRef(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);
  const heroNoteY = useTransform(scrollYProgress, [0, 1], ["0%", "70%"]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisRef.current = null; };
  }, []);

  useEffect(() => {
    fetch(`${API}/properties/meta/neighborhoods`)
      .then((res) => (res.ok ? res.json() : null))
      .then((list) => { if (Array.isArray(list) && list.length) setNeighborhoods(list); })
      .catch(() => {});
  }, []);

  const loadProperties = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ property_type: activeFilter });
      if (search.trim()) params.set("search", search.trim());
      if (neighborhood !== "All") params.set("neighborhood", neighborhood);
      if (beds !== "Any") params.set("min_beds", beds.replace("+", ""));
      const band = PRICE_BANDS.find((item) => item.label === priceBand);
      if (band?.min) params.set("min_price", band.min);
      if (band?.max) params.set("max_price", band.max);
      const response = await fetch(`${API}/properties?${params.toString()}`);
      if (!response.ok) throw new Error("Unable to load properties");
      setProperties(await response.json());
    } catch {
      toast.error("We could not refresh the collection. Please try again.");
      setProperties(FALLBACK_PROPERTIES.filter((property) => activeFilter === "All" || property.type === activeFilter));
    } finally {
      setLoading(false);
    }
  }, [activeFilter, search, neighborhood, beds, priceBand]);

  useEffect(() => { const timer = setTimeout(loadProperties, 350); return () => clearTimeout(timer); }, [loadProperties]);
  useEffect(() => { localStorage.setItem("brk-shortlist", JSON.stringify(shortlist)); }, [shortlist]);

  const jump = (id) => {
    const target = document.getElementById(id);
    if (lenisRef.current && target) lenisRef.current.scrollTo(target, { offset: -70, duration: 1.2 });
    else target?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const isSaved = (id) => shortlist.some((item) => item.id === id);
  const toggleShortlist = (property) => {
    const saved = isSaved(property.id);
    setShortlist((list) => (saved ? list.filter((item) => item.id !== property.id) : [...list, property]));
    toast(saved ? "Removed from your shortlist." : "Saved to your private shortlist.");
  };

  const downloadReport = () => {
    const report = `BRK PROPERTIES\nTHE MERIDIAN · DUBAI PROPERTY OUTLOOK · Q3 2026\n\nA considered view of the market\n\nPRIME RESIDENTIAL\nDemand continues to favour well-connected, design-led residences with strong outdoor space.\n\nOUR LENS\nWe look beyond headline growth: liquidity, quality of finish, service charges, and the life a place enables.\n\nSpeak with BRK Properties\nhello@brkproperties.com · +971 4 500 1508`;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([report], { type: "text/plain" }));
    link.download = "brk-meridian-outlook-q3-2026.txt";
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("The Meridian outlook is downloading.");
  };

  return (
    <div className="site-shell">
      <Toaster position="bottom-right" toastOptions={{ style: { background: "#f6f1e9", color: "#151918", border: "1px solid #d4af37" } }} />
      <nav className="nav-wrap" data-testid="main-navigation">
        <button className="brand" onClick={() => jump("top")} data-testid="brand-home">
          <LogoMark size={30} />
          <span className="brand-word"><span>BRK</span><small>PROPERTIES</small></span>
        </button>
        <div className={`nav-links ${menuOpen ? "is-open" : ""}`}>
          <button onClick={() => jump("properties")} data-testid="nav-properties">Properties</button>
          <button onClick={() => jump("approach")} data-testid="nav-approach">Our approach</button>
          <button onClick={() => jump("services")} data-testid="nav-services">Consultancy</button>
          <button className="nav-mobile-cta" onClick={() => setModal("consultation")} data-testid="nav-mobile-consultation">Book a consultation</button>
        </div>
        <button className="outline-cta" onClick={() => setModal("consultation")} data-testid="nav-consultation">Book a consultation <ArrowUpRight size={15} /></button>
        <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu" data-testid="mobile-menu-button">{menuOpen ? <X /> : <Menu />}</button>
      </nav>
      <main id="top">
        <section className="hero section-pad" ref={heroRef} data-testid="hero-section">
          <div className="hero-copy">
            <motion.p className="eyebrow" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1, ease: EASE }}>PROPERTY, WITH PERSPECTIVE</motion.p>
            <h1>
              <MaskLine delay={0.25}>Find a place</MaskLine>
              <MaskLine delay={0.42}><em>worth keeping.</em></MaskLine>
            </h1>
            <motion.p className="hero-text" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, delay: 0.8, ease: EASE }}>BRK Properties is a considered approach to property dealing and consultancy. We find the spaces that feel right — and make the numbers make sense.</motion.p>
            <motion.div className="hero-actions" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, delay: 0.95, ease: EASE }}>
              <button className="gold-btn" onClick={() => jump("properties")} data-testid="explore-properties-button">Explore properties <ArrowUpRight size={17} /></button>
              <button className="play-link" onClick={() => toast("A private BRK tour is being prepared for you.")} data-testid="watch-story-button"><span className="play-icon"><Play size={12} fill="currentColor" /></span> Watch our story</button>
            </motion.div>
          </div>
          <div className="hero-visual">
            <motion.div className="hero-frame" initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} transition={{ duration: 1.25, delay: 0.5, ease: EASE }}>
              <motion.img src={IMAGES.hero} alt="Minimalist modern villa surrounded by greenery" style={{ y: heroImgY }} data-testid="hero-image" />
            </motion.div>
            <motion.div className="hero-caption" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 1.4 }}><span>01 / 03</span><span>Al Barari, Dubai</span></motion.div>
          </div>
          <motion.div className="hero-side-note" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 1.5 }} style={{ y: heroNoteY }}>EST. 2008 <span>SCROLL TO EXPLORE ↓</span></motion.div>
        </section>

        <Marquee items={MARQUEE_ITEMS} />

        <section className="statement section-pad" id="approach" data-testid="approach-section">
          <Reveal><div className="section-kicker">01 <span>THE BRK DIFFERENCE</span></div></Reveal>
          <Reveal delay={0.12}>
            <div className="statement-body">
              <h2>Property is personal.<br /><span>Our advice is, too.</span></h2>
              <p>Beyond transactions, we offer clarity. From a first viewing to a considered portfolio, our role is to listen closely, look further and act with your long-term interests in mind.</p>
              <button className="text-link" onClick={() => toast("Our story is coming soon — ask us over coffee.")} data-testid="our-story-link">Discover our story <ChevronRight size={16} /></button>
            </div>
          </Reveal>
        </section>

        <section className="property-section section-pad" id="properties" data-testid="properties-section">
          <Reveal>
            <div className="section-heading">
              <div><div className="section-kicker">02 <span>CURATED COLLECTION</span></div><h2>Selected properties</h2></div>
              <button className="text-link" onClick={() => setModal("shortlist")} data-testid="view-shortlist-button">View shortlist ({shortlist.length}) <ArrowUpRight size={16} /></button>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="property-toolbar">
              <div className="filter-row" data-testid="property-filters">
                {["All", "Villa", "Apartment"].map((filter) => (
                  <button key={filter} className={activeFilter === filter ? "active" : ""} onClick={() => setActiveFilter(filter)} data-testid={`filter-${filter.toLowerCase()}`}>{filter}</button>
                ))}
              </div>
              <label className="search-box"><Search size={15} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search location or name" aria-label="Search properties" data-testid="property-search" /></label>
            </div>
            <div className="filter-row advanced" data-testid="advanced-filters">
              <label className="filter-select">
                <span>Neighbourhood</span>
                <select value={neighborhood} onChange={(event) => setNeighborhood(event.target.value)} aria-label="Filter by neighbourhood" data-testid="filter-neighborhood">
                  <option value="All">All areas</option>
                  {neighborhoods.map((area) => <option key={area} value={area}>{area}</option>)}
                </select>
              </label>
              <label className="filter-select">
                <span>Bedrooms</span>
                <select value={beds} onChange={(event) => setBeds(event.target.value)} aria-label="Filter by bedrooms" data-testid="filter-bedrooms">
                  {BED_OPTIONS.map((option) => <option key={option} value={option}>{option === "Any" ? "Any" : `${option} bedrooms`}</option>)}
                </select>
              </label>
              <label className="filter-select">
                <span>Price</span>
                <select value={priceBand} onChange={(event) => setPriceBand(event.target.value)} aria-label="Filter by price" data-testid="filter-price">
                  {PRICE_BANDS.map((band) => <option key={band.label} value={band.label}>{band.label}</option>)}
                </select>
              </label>
            </div>
          </Reveal>
          <div className="property-grid">
            {loading ? (
              <p className="loading-state" data-testid="properties-loading">Refreshing the collection…</p>
            ) : properties.length === 0 ? (
              <p className="loading-state" data-testid="properties-empty">No properties match that search.</p>
            ) : (
              properties.map((property, index) => (
                <motion.article
                  className={`property-card ${index === 0 ? "featured-card" : ""}`}
                  key={property.id}
                  initial={{ opacity: 0, y: 36 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-6% 0px" }}
                  transition={{ duration: 0.75, delay: Math.min(index * 0.09, 0.45), ease: EASE }}
                  data-testid={`property-card-${property.id}`}
                >
                  <div className="property-image">
                    <img src={property.image} alt={property.name} />
                    <span className="property-tag">{property.status}</span>
                    <button className={`image-arrow ${isSaved(property.id) ? "saved" : ""}`} onClick={() => toggleShortlist(property)} aria-label={`${isSaved(property.id) ? "Remove" : "Save"} ${property.name}`} data-testid={`save-property-${property.id}`}>
                      <Heart size={16} fill={isSaved(property.id) ? "currentColor" : "none"} />
                    </button>
                  </div>
                  <button className="property-detail-trigger" onClick={() => setModal({ type: "property", property })} data-testid={`property-detail-${property.id}`}>
                    <div className="property-meta">
                      <div><h3>{property.name}</h3><p>{property.location}</p></div>
                      <strong>{property.price}</strong>
                    </div>
                    <div className="property-specs"><span>{property.beds} bedrooms</span><span>{property.area}</span><span>View details ↗</span></div>
                  </button>
                </motion.article>
              ))
            )}
          </div>
        </section>

        <section className="services-section section-pad" id="services" data-testid="services-section">
          <Reveal><div className="section-kicker">03 <span>BEYOND THE DEAL</span></div></Reveal>
          <Reveal delay={0.1}>
            <div className="services-intro">
              <h2>Advice for the<br /><em>long view.</em></h2>
              <p>A property decision rarely starts and ends with a key handover. Our consultancy brings together the right thinking, people and perspective for every chapter.</p>
            </div>
          </Reveal>
          <div className="service-list">
            {["Acquisition strategy", "Portfolio management", "Tax & legal guidance", "Interior & architecture"].map((service, i) => (
              <Reveal key={service} delay={i * 0.06} y={18}>
                <button onClick={() => setModal("consultation")} data-testid={`service-${i + 1}`}><span>0{i + 1}</span>{service}<ArrowUpRight size={18} /></button>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="numbers-section section-pad" data-testid="trust-section">
          <Reveal className="numbers-image"><img src={IMAGES.living} alt="Warm modern living room" /></Reveal>
          <Reveal delay={0.12}>
            <div className="numbers-copy">
              <div className="section-kicker">04 <span>THE NUMBERS</span></div>
              <h2>Trust, built<br /><em>over time.</em></h2>
              <div className="stats">
                <div><strong>16</strong><span>years of<br />perspective</span></div>
                <div><strong>38</strong><span>cities across<br />the region</span></div>
                <div><strong>94%</strong><span>client referrals<br />and return visits</span></div>
              </div>
              <button className="gold-btn" onClick={() => setModal("calculator")} data-testid="open-calculator-button">Explore your potential <ArrowUpRight size={17} /></button>
            </div>
          </Reveal>
        </section>

        <section className="report-section section-pad" data-testid="market-report-section">
          <Reveal>
            <div><p className="eyebrow">05 / THE MERIDIAN</p><h2>The considered<br /><em>market report.</em></h2></div>
          </Reveal>
          <Reveal delay={0.12}>
            <div>
              <p>The Meridian is our quarterly Dubai property outlook — it distils the signals behind the headlines: where opportunity is forming, what quality looks like, and how to move with intent.</p>
              <button className="gold-btn" onClick={downloadReport} data-testid="download-market-report"><Download size={16} /> Download the Q3 outlook</button>
            </div>
          </Reveal>
        </section>

        <footer className="footer section-pad">
          <div className="footer-brand"><LogoMark size={36} className="footer-logo" /><span>BRK</span><p>Property, with perspective.</p></div>
          <div className="footer-links">
            <div><p className="eyebrow">CONTACT</p><a href="mailto:hello@brkproperties.com" data-testid="footer-email">hello@brkproperties.com</a><a href="tel:+97145001508" data-testid="footer-phone">+971 4 500 1508</a></div>
            <div><p className="eyebrow">VISIT</p><p>Gate Village 03<br />DIFC, Dubai, UAE</p></div>
            <div><p className="eyebrow">FOLLOW</p><a href="#top" data-testid="footer-instagram">Instagram</a><a href="#top" data-testid="footer-linkedin">LinkedIn</a></div>
          </div>
          <div className="footer-bottom"><span>© 2026 BRK Properties</span><a href="/admin" className="footer-admin-link" data-testid="footer-admin-link">Private office</a><span>Made for the considered life.</span></div>
        </footer>
      </main>

      {modal === "consultation" && <ConsultModal onClose={() => setModal(null)} />}
      {modal === "calculator" && <CalculatorModal onClose={() => setModal(null)} onConsult={() => setModal("consultation")} />}
      {modal === "shortlist" && (
        <ShortlistModal
          shortlist={shortlist}
          onClose={() => setModal(null)}
          onOpenProperty={(property) => setModal({ type: "property", property })}
          onRemove={(id) => setShortlist((list) => list.filter((item) => item.id !== id))}
          onConsult={() => setModal("consultation")}
        />
      )}
      {modal?.type === "property" && <PropertyModal property={modal.property} onClose={() => setModal(null)} onEnquire={() => setModal("consultation")} />}
    </div>
  );
}

export default Site;
